import React, { useEffect, useRef, useState } from 'react'

import 'react-circular-progressbar/dist/styles.css'
import { CircularProgressbar } from 'react-circular-progressbar'
import { Line } from 'react-chartjs-2'
import '../../App.css'
import { Icon } from '../../components/Icon'
import LineProgressBar from './components/LineProgressBar'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { useQuery } from 'react-query'
import {
  getPolicyCount,
  getProfileStatsCount,
  getSignupApiCount,
  getStatsCount,
  getWhistleAndIncident,
  PolicyTrackingItem,
} from './apiCalls'

import { calculatePercentage, processData } from './utils'
import { useToggledSwitches } from '../../context/ToggledSwitchesContext'
import {
  capitalizeFirstLetter,
  formatDate,
  statusBgColorWhistleblowing,
} from '../../utils/utils'
import DashboardMenu from '../../components/UI/DashboardMenu'
import Loading from '../../components/UI/Loading'
import { SeverityIcon } from '../lead/incidents/utils'
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

const Dashboard = () => {
  const { toggledSwitches } = useToggledSwitches()
  const pdfRef = useRef<HTMLDivElement>(null)
  function getStoredIds(key: string): string[] {
    // Retrieve the JSON string from local storage
    const storedIdsString = localStorage.getItem(key)

    // Parse the JSON string and return it as an array
    if (storedIdsString) {
      try {
        const storedIds = JSON.parse(storedIdsString)

        // Ensure that the parsed data is an array of strings
        if (
          Array.isArray(storedIds) &&
          storedIds.every((id) => typeof id === 'string')
        ) {
          return storedIds
        } else {
          return []
        }
      } catch (error) {
        return []
      }
    }

    // Return an empty array if there's no data in local storage
    return []
  }

  const schoolIds = getStoredIds('SchoolIds')
  const wrapperRefScrollMenu = useRef<HTMLDivElement>(null)
  const [userFilter, setUserFilter] = useState({
    key: 'active',
    value: 'active',
  })
  const [isOpenMenuButton, setIsOpenMenuButton] = useState(false)
  const getSignUpsCount = useQuery('signupCount', () =>
    getSignupApiCount(schoolIds)
  )
  const getWhistleIncident = useQuery(['whistle', 'incident'], () =>
    getWhistleAndIncident(toggledSwitches.length > 0 ? toggledSwitches : schoolIds)
  )
  const [counts, months] = processData(getSignUpsCount?.data || [])
  const incidents = processData(
    getWhistleIncident?.data?.graph?.Incidents || []
  )
  const whistleBlowing = processData(
    getWhistleIncident?.data?.graph?.whistleBlowing || []
  )

  const policyCount = useQuery(['policies', 1], () =>
    getPolicyCount(toggledSwitches.length > 0 ? toggledSwitches : schoolIds)
  )

  const statsCount = useQuery(['stats', 2], () =>
    getStatsCount(toggledSwitches.length > 0 ? toggledSwitches : schoolIds)
  )

  const profileStatsCount = useQuery(['profileStats', 3], () =>
    getProfileStatsCount(toggledSwitches.length > 0 ? toggledSwitches : schoolIds)
  )

  useEffect(() => {
    getWhistleIncident.refetch()
    policyCount.refetch()
    statsCount.refetch()
    profileStatsCount.refetch()
  }, [toggledSwitches])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      // Check if the click is outside the component
      if (
        wrapperRefScrollMenu.current &&
        !wrapperRefScrollMenu.current.contains(event.target as Node)
      ) {
        setIsOpenMenuButton(false) // Close the dropdown if it's open
      }
    }
    // Add event listener for clicks outside the component
    document.addEventListener('mousedown', handleOutsideClick)
    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        position: 'left' as const, // Correct type definition for position
        ticks: {
          padding: 10,
          precision: 0, // Display only integer values
        },
        grid: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const, // Correct type definition for position
        display: false,
      },
      title: {
        display: false,
        text: 'Chart.js Line Chart',
      },
    },
  }
  const options2 = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        position: 'left' as const, // Correct type definition for position
        ticks: {
          padding: 10,
          precision: 0, // Display only integer values
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const, // Correct type definition for position
        display: false,
      },
      title: {
        display: false,
        text: 'Chart.js Line Chart',
      },
    },
  }

  const data = {
    labels: incidents[1],
    datasets: [
      {
        label: 'Incidents',
        data: incidents[0],
        fill: true,
        backgroundColor: 'rgba(2, 105, 140, 0.2)',
        borderColor: '#02698C',
        borderWidth: 1,
      },
      {
        label: 'Whistleblowing',
        data: whistleBlowing[0],
        fill: true,
        backgroundColor: 'rgba(255, 168, 0, 0.4)',
        borderColor: 'rgba(255, 168, 0, 1)',
        borderWidth: 1,
      },
    ],
  }

  const singleLineData = {
    labels: months,
    datasets: [
      {
        label: '',
        data: counts,
        fill: true,
        backgroundColor: 'rgba(2, 105, 140, 0.2)',
        borderColor: '#02698C',
        borderWidth: 1,
      },
    ],
  }

  const totalProfileComplete =
    profileStatsCount?.data?.totalProfileComplete || 0
  const totalSafeguardingComplete =
    profileStatsCount?.data?.totalSafeguardingRecordComplete || 0

  const totalTraningComplete =
    profileStatsCount?.data?.totalTrainingRecordComplete || 0

  function getCountByStatus(status: string): string {
    const user = statsCount?.data?.users?.find(
      (user: { status: string; count: string }) => user.status === status
    )
    return user ? user.count : '0'
  }

  const homeCards = [
    {
      title: capitalizeFirstLetter(userFilter.key),
      value: getCountByStatus(userFilter.value),
      descriptionValue: `${Math.round(totalProfileComplete)}%`,
      description: 'completed account',
      iconName: 'IconUsersCheck',
      dropDown: true,
      isLoading: statsCount?.isLoading || !statsCount?.data,
    },
    {
      title: 'Monthly Meetings',
      value: statsCount?.data?.meetings?.count,
      descriptionValue: statsCount?.data?.lastMonthMeetings?.count,
      description: ' last month',
      iconName: 'IconMessageChatSquareInActive',
      dropDown: false,
      isLoading: statsCount?.isLoading || !statsCount?.data,
    },
    {
      title: 'Open Incidents',
      value: statsCount?.data?.incidents.count,
      descriptionValue: statsCount?.data?.incidentsClosedLastWeek?.count,
      description: 'closed last week',
      iconName: 'IconAlertTriangle',
      dropDown: false,
      isLoading: statsCount?.isLoading || !statsCount?.data,
    },
    {
      title: 'Whistleblower Reports',
      value: statsCount?.data?.whistleBlowingReports?.count,
      descriptionValue: statsCount?.data?.whistleBlowingClosedLastWeek?.count,
      description: 'closed last week',
      iconName: 'IconAnnotationAlertInActive',
      dropDown: false,
      isLoading: statsCount?.isLoading || !statsCount?.data,
    },
  ]

  return (
    <>
      <div
        className={
          'flex flex-col gap-6 w-full  h-full bg-white   rounded-3xl p-8 '
        }
      >
        <div className="flex flex-col gap-6 w-full relative h-full">
          <div className="flex w-full justify-between items-start">
            <div className="flex-col w-[50%] gap-[20px]">
              <p className="text-lg leading-6 font-bold text-text2">
                Your Dashboard
              </p>
            </div>
            <div className={'flex flex-row gap-[15px]'}></div>
          </div>
          <div
            ref={pdfRef}
            style={{
              height: 'calc(100vh - 100px)',
              overflow: 'auto',
              // scrollbarWidth: 'none',
            }}
            className="overflow-hidden flex flex-col gap-4 mt-6"
          >
            {/* Section 1 */}
            <div className="flex gap-4">
              {homeCards?.map((item, index) => {
                return (
                  <>
                    {item.isLoading == true ? (
                      <div
                        key={index}
                        className="flex-1 bg-transparent border border-gray2 rounded-xl p-4"
                      >
                        <Loading size="small" />
                      </div>
                    ) : (
                      <div
                        key={index}
                        className="flex-1 bg-transparent border border-gray2 rounded-xl p-4"
                      >
                        <div className="flex">
                          <div className="flex-1 flex flex-col gap-2">
                            <div className="flex w-[95%] justify-between items-center">
                              <p className="text-sm font-medium leading-6 text-bluishGreen">
                                {item?.title}
                              </p>
                              {item.dropDown == true && (
                                <DashboardMenu
                                  wrapperRefScrollMenu={wrapperRefScrollMenu}
                                  isOpen={isOpenMenuButton}
                                  items={[
                                    {
                                      label: 'Active',
                                      onClick: () => {
                                        setUserFilter({
                                          key: 'active',
                                          value: 'active',
                                        })
                                        setIsOpenMenuButton(false)
                                      },
                                      icon: '',
                                      iconColor: '#02698C',
                                    },
                                    {
                                      label: 'Archived',
                                      onClick: () => {
                                        setUserFilter({
                                          key: 'archived',
                                          value: 'archive',
                                        })
                                        setIsOpenMenuButton(false)
                                      },
                                      icon: '',
                                      iconColor: '#02698C',
                                    },
                                    {
                                      label: 'Inactive',
                                      onClick: () => {
                                        setUserFilter({
                                          key: 'inactive',
                                          value: 'inactive',
                                        })
                                        setIsOpenMenuButton(false)
                                      },
                                      icon: '',
                                      iconColor: '#02698C',
                                    },
                                    {
                                      label: 'Suspended',
                                      onClick: () => {
                                        setUserFilter({
                                          key: 'suspended',
                                          value: 'suspended',
                                        })
                                        setIsOpenMenuButton(false)
                                      },
                                      icon: '',
                                      iconColor: '#02698C',
                                    },
                                  ]}
                                >
                                  <div
                                    onClick={() =>
                                      setIsOpenMenuButton(!isOpenMenuButton)
                                    }
                                    className={` text-white  flex  cursor-pointer bg-transparent`}
                                  >
                                    <Icon
                                      name={'IconArrowDown'}
                                      strokeWidth={1.2}
                                      stroke={'#02698C'}
                                      width={16}
                                      height={16}
                                    />
                                  </div>
                                </DashboardMenu>
                              )}
                            </div>

                            <p className="text-[28px] font-bold leading-9 text-text2">
                              {item?.value}
                            </p>
                          </div>
                          <div className="flex justify-center items-center bg-gray1 rounded-lg w-12 h-12">
                            <Icon
                              name={item?.iconName}
                              strokeWidth={1}
                              stroke={'#02698C'}
                              width={28}
                              height={28}
                            />
                          </div>
                        </div>
                        <p className="text-sm font-medium leading-6 text-text3 mt-3">
                          <span className="text-sm font-bold leading-6 text-green">
                            {item?.descriptionValue}
                          </span>{' '}
                          {item?.description}
                        </p>
                      </div>
                    )}
                  </>
                )
              })}
            </div>
            {/* Section 2 */}
            <div className="flex gap-4">
              <div className="flex flex-col flex-1 bg-transparent border border-gray2 rounded-xl py-5 px-6">
                <div>
                  <p className="text-sm font-medium leading-6 text-bluishGreen">
                    Policy Tracker
                  </p>
                </div>
                <div className="flex flex-col gap-8 mt-6 justify-center h-full">
                  <div className="flex justify-end gap-[2px]">
                    <p className="w-20 text-xs font-medium leading-5 text-text2">
                      Published
                    </p>
                    <LineProgressBar
                      percentage={Number(
                        calculatePercentage(
                          (policyCount?.data
                            ?.policyTracking as PolicyTrackingItem) || {}, // Type assertion
                          'publish'
                        )
                      )}
                      value={0}
                      stroke={'#01B38B'}
                      strokeWidth={4}
                      isPercentage={true}
                    />
                  </div>

                  <div className="flex justify-end gap-[2px]">
                    <p className="w-20 text-xs font-medium leading-5 text-text2">
                      Unpublished
                    </p>
                    <LineProgressBar
                      percentage={Number(
                        calculatePercentage(
                          (policyCount?.data
                            ?.policyTracking as PolicyTrackingItem) || {}, // Type assertion
                          'unpublish'
                        )
                      )}
                      value={0}
                      stroke={'#FFA800'}
                      strokeWidth={4}
                      isPercentage={true}
                    />
                  </div>
                  <div className="flex justify-end gap-[2px]">
                    <p className="w-20 text-xs font-medium leading-5 text-text2">
                      Expired
                    </p>
                    <LineProgressBar
                      percentage={Number(
                        calculatePercentage(
                          (policyCount?.data
                            ?.policyTracking as PolicyTrackingItem) || {}, // Type assertion
                          'expired'
                        )
                      )}
                      value={0}
                      stroke={'#FF6666'}
                      strokeWidth={4}
                      isPercentage={true}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col flex-1 bg-transparent border border-gray2 rounded-xl py-5 px-6">
                <div>
                  <p className="text-sm font-medium leading-6 text-bluishGreen">
                    Upcoming Expiring Policies
                  </p>
                </div>
                <div className="flex flex-col gap-2 mt-6 ">
                  {policyCount?.data?.nearExpiryPolicies?.map((item) => {
                    return (
                      <>
                        <div className="flex gap-3 items-center">
                          <div className="flex justify-center items-center bg-textErrorLight rounded-lg w-6 h-6">
                            <Icon
                              name={'IconPDFRed'}
                              strokeWidth={1.2}
                              // stroke={'#FF6666'}
                              width={16}
                              height={16}
                            />
                          </div>
                          <p className="flex-1 text-xs leading-5 font-medium text-text2">
                            {item.title}
                          </p>
                          <div className="rounded-full w-[100px] justify-center flex bg-textError p-1 px-2">
                            <p className="text-xs leading-5 font-medium text-text8">
                              {formatDate(item.nextReviewDate)}
                            </p>
                          </div>
                        </div>
                      </>
                    )
                  })}
                </div>
              </div>
            </div>
            {/* Section 3 */}
            <div className="flex gap-4">
              <div className="w-[320px] bg-transparent border border-gray2 rounded-xl py-5 px-6">
                <div>
                  <p className="text-sm font-medium leading-6 text-bluishGreen">
                    Completed Profiles
                  </p>
                </div>
                <div className="flex items-center justify-center mt-8">
                  <div className="w-[160px] h-[160px]">
                    <CircularProgressbar
                      strokeWidth={20}
                      value={Math.round(totalProfileComplete) || 0}
                      styles={{
                        root: {},
                        trail: {
                          stroke: '#013546',
                          strokeLinecap: 'butt',
                          // Rotate the path
                          transform: 'rotate(0.25turn)',
                          transformOrigin: 'center center',
                        },
                        path: {
                          stroke: `#01B38B`,
                          transition: 'stroke-dashoffset 0.5s ease 0s',
                          strokeLinecap: 'butt',
                          // Rotate the path
                          transform: 'rotate(0.25turn)',
                          transformOrigin: 'center center',
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="flex justify-evenly mt-6">
                  <div className="flex items-center flex-col gap-1">
                    <div className="bg-text2 rounded-full px-2 py-[6px]">
                      <p className="text-xs font-medium leading-4 text-white">
                        {Number(totalProfileComplete) == 0
                          ? 100 - Math.round(totalProfileComplete)
                          : 100 - Math.round(totalProfileComplete)}
                        %
                      </p>
                    </div>
                    <p className="text-xs font-medium leading-4 text-bluishGreen">
                      Incomplete
                    </p>
                  </div>
                  <div className="flex items-center flex-col gap-1">
                    <div className="bg-green rounded-full px-2 py-[6px]">
                      <p className="text-xs font-medium leading-4 text-white">
                        {Math.round(totalProfileComplete)}%
                      </p>
                    </div>
                    <p className="text-xs font-medium leading-4 text-bluishGreen">
                      Complete
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col flex-1 bg-transparent border border-gray2 rounded-xl py-5 px-6">
                <div>
                  <p className="text-sm font-medium leading-6 text-bluishGreen">
                    Profile Completion Breakdown
                  </p>
                </div>
                <div className="h-[200px] flex flex-col gap-7 mt-6 justify-center">
                  <div className="flex gap-8">
                    <p className="w-32 text-xs font-medium leading-5 text-text2">
                      Profile
                    </p>
                    <LineProgressBar
                      percentage={Math.round(totalProfileComplete)}
                      value={Math.round(totalProfileComplete)}
                      stroke={'#99C3D1'}
                      strokeWidth={4}
                      isPercentage={true}
                      textStyle={'ml-5'}
                    />
                  </div>
                  <div className="flex gap-8">
                    <p className="w-32 text-xs font-medium leading-5 text-text2">
                      Safeguarding
                    </p>
                    <LineProgressBar
                      percentage={Math.round(totalSafeguardingComplete)}
                      value={Math.round(totalSafeguardingComplete)}
                      stroke={'#01698B'}
                      strokeWidth={4}
                      isPercentage={true}
                      textStyle={'ml-5'}
                    />
                  </div>
                  <div className="flex gap-8">
                    <p className="w-32 text-xs font-medium leading-5 text-text2">
                      Training
                    </p>
                    <LineProgressBar
                      percentage={Math.round(totalTraningComplete)}
                      value={Math.round(totalTraningComplete)}
                      stroke={'#013546'}
                      strokeWidth={4}
                      isPercentage={true}
                      textStyle={'ml-5'}
                    />
                  </div>
                </div>
              </div>
              <div className="flex-1 bg-transparent border border-gray2 rounded-xl py-5 px-6">
                <div>
                  <p className="text-sm font-medium leading-6 text-bluishGreen">
                    Number of Sign Ups
                  </p>
                </div>
                <div className="flex cursor-pointer mt-12">
                  <Line
                    options={options2}
                    data={singleLineData}
                    width={'100%'}
                    height={260}
                  />
                </div>
              </div>
            </div>
            {/* Section 4 */}
            <div className="flex gap-4 w-full">
              <div className="flex flex-col flex-1 bg-transparent border border-gray2 rounded-xl py-5 px-6 h-96">
                <div>
                  <p className="text-sm font-medium leading-6 text-bluishGreen">
                    Incidents vs Whistleblowing Reports
                  </p>
                </div>
                <div className="flex h-[300px] mt-8">
                  <Line options={options} data={data} width={'100%'} />
                </div>
              </div>
            </div>
            {/* Section 6 */}

            {/* Section 6 */}
            <div className="flex gap-4">
              <div className="flex flex-col flex-1 bg-transparent border border-gray2 rounded-xl py-5 px-6">
                <div>
                  <p className="text-sm font-medium leading-6 text-bluishGreen">
                    Incident Reports
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 mt-6">
                  {getWhistleIncident?.data?.whistleBlowing?.map(
                    (item, index) => {
                      return (
                        <>
                          <div
                            key={index}
                            className="w-[32%] p-4 bg-gray1 rounded-xl flex flex-col gap-[6px]"
                          >
                            <div className="flex gap-4 items-center">
                              <p className="w-[70px] text-text1 text-sm leading-6 font-semibold">
                                Report ID:
                              </p>
                              <p className="text-bluishGreen text-sm leading-6 font-semibold flex-1">
                                {item?.reportNo}
                              </p>
                            </div>
                            <div className="flex gap-4 items-center">
                              <p className="w-[70px] text-text1 text-sm leading-6 font-semibold">
                                Severity Level:
                              </p>
                              <p className="text-bluishGreen text-sm leading-6 font-semibold flex-1 overflow-hidden whitespace-nowrap overflow-ellipsis">
                                {/* {item?.severity_level} */}
                                <SeverityIcon
                                  severity_level={item.severity_level}
                                />
                              </p>
                            </div>
                            <div className="flex gap-4 items-center">
                              <p className="w-[70px] text-text1 text-sm leading-6 font-semibold">
                                Status:
                              </p>
                              <div
                                className={`bg-${statusBgColorWhistleblowing(item?.status)} px-3 py-2 rounded-[40px]`}
                              >
                                <p
                                  className={`text-text2 text-xs leading-5 font-medium`}
                                >
                                  {capitalizeFirstLetter(item?.status || '')}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2 items-center">
                              <p className="w-[70px] text-green text-[10px] leading-4 font-semibold">
                                School Name
                              </p>
                              <p>•</p>
                              <p className="text-text3 text-[10px] leading-4 font-semibold">
                                {item?.schoolName}
                              </p>
                            </div>
                          </div>
                        </>
                      )
                    }
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col flex-1 bg-transparent border border-gray2 rounded-xl py-5 px-6">
                <div>
                  <p className="text-sm font-medium leading-6 text-bluishGreen">
                    Whistleblowing Reports
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 mt-6">
                  {getWhistleIncident?.data?.Incident?.map((item, index) => {
                    return (
                      <>
                        <div
                          key={index}
                          className="w-[32%] p-4 bg-gray1 rounded-xl flex flex-col gap-[6px]"
                        >
                          <div className="flex gap-4 items-center">
                            <p className="w-[70px] text-text1 text-sm leading-6 font-semibold">
                              Report ID:
                            </p>
                            <p className="text-bluishGreen text-sm leading-6 font-semibold flex-1">
                              {item?.reportNo}
                            </p>
                          </div>
                          <div className="flex gap-4 items-center">
                            <p className="w-[70px] text-text1 text-sm leading-6 font-semibold">
                              Subject:
                            </p>
                            <p className="text-bluishGreen text-sm leading-6 font-semibold flex-1 overflow-hidden whitespace-nowrap overflow-ellipsis">
                              {item?.subject}
                            </p>
                          </div>
                          <div className="flex gap-4 items-center">
                            <p className="w-[70px] text-text1 text-sm leading-6 font-semibold">
                              Status:
                            </p>
                            <div
                              className={`bg-${statusBgColorWhistleblowing(item?.status)} px-3 py-2 rounded-[40px]`}
                            >
                              <p
                                className={`text-text2 text-xs leading-5 font-medium`}
                              >
                                {capitalizeFirstLetter(item?.status || '')}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 items-center">
                            <p className="w-[70px] text-green text-[10px] leading-4 font-semibold">
                              School Name
                            </p>
                            <p>•</p>
                            <p className="text-text3 text-[10px] leading-4 font-semibold">
                              {item?.schoolName}
                            </p>
                          </div>
                        </div>
                      </>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Dashboard
